// src/mobile/hooks/useKeyboardScanner.ts
import { useEffect, useRef } from 'react'

type Opts = {
    minLen?: number
    gapMs?: number
    timeoutMs?: number
    preventDefault?: boolean
}

const _isTextInput = (t: EventTarget | null) => {
    const el = t as HTMLElement | null
    if (!el) return false
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return true
    if (el.isContentEditable) return true
    return false
}

export function useKeyboardScanner(onRead: (code: string) => void, opts: Opts = {}) {
    const buf = useRef<string>('')         // buffer atual
    const t = useRef<number | null>(null)  // timeout p/ finalizar
    const lastTs = useRef<number>(0)       // último timestamp

    useEffect(() => {
        const minLen = opts.minLen ?? 2
        const gapMs = opts.gapMs ?? 50          // um pouco mais tolerante
        const timeoutMs = opts.timeoutMs ?? 60      // idem
        const preventDef = opts.preventDefault ?? true

        const terminators = new Set(['Enter', 'NumpadEnter', 'Tab'])

        const resetTimer = () => {
            if (t.current) {
                window.clearTimeout(t.current)
                t.current = null
            }
        }

        const finishIfAny = () => {
            const raw = buf.current
            resetTimer()
            buf.current = ''
            lastTs.current = 0

            if (!raw) return
            const code = raw.replace(/[\r\n]+$/g, '') // remove ENTER final
            if (code.length < minLen) return
            onRead(code)
        }

        const scheduleFinish = () => {
            resetTimer()
            t.current = window.setTimeout(finishIfAny, timeoutMs)
        }

        const maybePrevent = (e: Event) => {
            if (!preventDef) return
                ; (e as any).preventDefault?.()
                ; (e as any).stopImmediatePropagation?.()
            e.stopPropagation()
        }

        /** IMPORTANTE: se foco está em input/textarea/contenteditable,
         *  não interceptamos nada. Assim o login (usuário/senha) não é afetado. */
        const focusIsEditable = () => {
            const a = document.activeElement as HTMLElement | null
            return !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (focusIsEditable()) return        // <<< mudança chave

            // ignorar atalhos
            if (e.altKey || e.ctrlKey || e.metaKey) return

            const now = performance.now()
            const delta = now - (lastTs.current || now)
            lastTs.current = now

            if (terminators.has(e.key)) {
                maybePrevent(e)
                finishIfAny()
                return
            }

            if (e.key === ' ') {                 // espaço
                buf.current += ' '
                maybePrevent(e)
                scheduleFinish()
                return
            }

            if (e.key.length === 1) {            // imprimível
                if (buf.current && delta > gapMs) finishIfAny()
                buf.current += e.key
                maybePrevent(e)
                scheduleFinish()
            }
        }

        /** Zebra às vezes injeta Enter como beforeinput → insertLineBreak */
        const onBeforeInput = (e: InputEvent) => {
            if (focusIsEditable()) return        // <<< não intercepta em inputs
            if (e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
                if (buf.current) {
                    maybePrevent(e)
                    finishIfAny()
                }
            }
        }

        const onBlur = () => finishIfAny()
        const onVisibility = () => {
            if (document.visibilityState !== 'visible') finishIfAny()
        }

        window.addEventListener('keydown', onKeyDown, { capture: true })
        document.addEventListener('beforeinput', onBeforeInput as any, { capture: true })
        window.addEventListener('blur', onBlur)
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            window.removeEventListener('keydown', onKeyDown, { capture: true } as any)
            document.removeEventListener('beforeinput', onBeforeInput as any, { capture: true })
            window.removeEventListener('blur', onBlur)
            document.removeEventListener('visibilitychange', onVisibility)
            resetTimer()
            buf.current = ''
        }
    }, [onRead, opts.minLen, opts.gapMs, opts.timeoutMs, opts.preventDefault])
}
